import { ReactElement, useRef, useState, useEffect, ChangeEvent, KeyboardEvent } from "react";
import { getTitle } from "../../shared/utils";
import { PageNumberData } from "../../shared/types";
import { PageNumber } from "@bronte/common";
import "./styles.scss";

export const NavigationBar = ({
  viewerApi,
  prevItem,
  nextItem,
  pageNumber,
  renderedId,
  currentItem,
  routeCompleted,
  spotlightAudio,
  shouldNavigateByPage,
}: any): ReactElement => {
  const audioRef = useRef<HTMLVideoElement | null>(null);
  const [ttsJSON, setTTSJSON] = useState<any>(null);
  const [audioURL, setAudioURL] = useState<string>("");
  const [isAudioPlayerVisible, setAudioPlayerVisible] = useState(false);
  const [targetPageNumber, setTargetPageNumber] = useState(pageNumber);
  const [pageNumberIndex, setPageNumberIndex] = useState(0);

  useEffect(() => {
    setTargetPageNumber(pageNumber);
  }, [pageNumber]);

  const handleEnter = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      const { screenId, dataId } = getPageNumberMap(targetPageNumber) || {};

      if (screenId && dataId) {
        viewerApi.route(screenId, { dataId });

        if (shouldNavigateByPage) {
          const pageNumberList = getPageNumbersByScreen(screenId);
          const pageNumberIndex = getCurrentPageIndex(pageNumberList, targetPageNumber);
          setPageNumberIndex(Number(pageNumberIndex));
        }
      } else {
        setTargetPageNumber(pageNumber);
        alert(`Dev Shell: ${targetPageNumber} is not a valid page number.`);
        console.error(`Dev Shell: ${targetPageNumber} is not a valid page number.`);
      }
    }
  };

  const handleAudioClick = () => {
    setAudioPlayerVisible(!isAudioPlayerVisible);
  };

  const getPageNumberMap = (pageNumber: string): PageNumberData | undefined => {
    const pageNumbers = window.pageNumbers;

    for (const screenId in pageNumbers) {
      for (const item of pageNumbers[screenId]) {
        if (item.dataId && item.pageNumber === pageNumber) {
          return {
            screenId,
            dataId: item.dataId,
          };
        }
      }
    }

    return;
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    setTargetPageNumber(value);
  };

  const getCurrentPageIndex = (pageNumberList: PageNumber[], pageNumber: string): number => {
    const pageNumberIndex = pageNumberList.findIndex(
      (obj: PageNumber) => obj.pageNumber === pageNumber,
    );
    return pageNumberIndex;
  };

  const navigateToSlate = (
    currIndex: number,
    pageNumberArr: PageNumber[],
    elementId: string,
  ): void => {
    const currDataId = pageNumberArr[currIndex]?.dataId;
    if (currDataId) {
      viewerApi.route(currentItem, { dataId: currDataId });
      setPageNumberIndex(currIndex);
    } else {
      viewerApi.route(elementId);
      setPageNumberIndex(0);
    }
  };

  const getPageNumbersByScreen = (elementId: string): PageNumber[] => {
    const pageNumbers = window.pageNumbers && window.pageNumbers[elementId];
    return (shouldNavigateByPage && pageNumbers) || [];
  };

  const onNext = (): void => {
    const pageNumberArr = getPageNumbersByScreen(currentItem);
    let currIndex = pageNumberIndex || 0;
    const pageNumberArrLen = pageNumberArr.length;

    if (shouldNavigateByPage && pageNumberArrLen > 1 && currIndex !== pageNumberArrLen - 1) {
      currIndex = getCurrentPageIndex(pageNumberArr, targetPageNumber);
      navigateToSlate(currIndex + 1, pageNumberArr, nextItem.elementId);
      return;
    }

    if (nextItem?.elementId) {
      viewerApi.route(nextItem.elementId);
      setPageNumberIndex(0);
    }
  };

  const onPrev = (): void => {
    const pageNumberArr = getPageNumbersByScreen(currentItem);
    let currIndex = pageNumberIndex || 0;

    if (shouldNavigateByPage && pageNumberArr?.length > 1 && currIndex > 0) {
      currIndex = getCurrentPageIndex(pageNumberArr, targetPageNumber);
      navigateToSlate(currIndex - 1, pageNumberArr, prevItem.elementId);
      return;
    }

    if (prevItem?.elementId) {
      const pageNumberArr = getPageNumbersByScreen(prevItem.elementId);
      const pageNumberArrLen = pageNumberArr.length;

      if (shouldNavigateByPage && pageNumberArrLen > 1) {
        viewerApi.route(prevItem.elementId, {
          dataId: pageNumberArr[pageNumberArrLen - 1]?.dataId,
        });
        const currIndex = getCurrentPageIndex(pageNumberArr, targetPageNumber);
        setPageNumberIndex(currIndex - 1);
      } else {
        viewerApi.route(prevItem.elementId);
        setPageNumberIndex(0);
      }
    }
  };

  //=================================================================================
  // AUDIO
  const fetchTTSAssets = () => {
    if (spotlightAudio?.ttsURL && renderedId) {
      fetch(`/proxy-tts?target=${spotlightAudio.ttsURL}/${renderedId}.json`)
        .then((r) => r.json())
        .then((json) => {
          setTTSJSON(json?.selectorElements ?? []);
          setAudioURL(`/proxy-tts?target=${spotlightAudio.ttsURL}/${renderedId}.mp3`);
        })
        .catch((e) => {
          console.error("Fetch TTS assets error", e);
        });
    }
  };

  useEffect(() => {
    if (renderedId && spotlightAudio && routeCompleted) {
      fetchTTSAssets();
    }
  }, [spotlightAudio, routeCompleted, renderedId]);

  useEffect(() => {
    const audioElement = audioRef.current;

    let spotlightsCount = 0;

    const handleTimeUpdate = () => {
      if (audioElement && ttsJSON) {
        const currentTime = audioElement.currentTime * 1000;

        for (const element of ttsJSON) {
          for (const sentence of element.sentences) {
            if (sentence.startOffset <= currentTime && currentTime <= sentence.endOffset) {
              for (const word of sentence.words) {
                if (word.startOffset <= currentTime && currentTime <= word.endOffset) {
                  const dataId = element.selector?.replace(/\[id="([a-z0-9\-]+)"\]/, "$1");

                  if (!dataId) {
                    return;
                  }

                  // stop playing if max spotlights exceeded
                  if (
                    spotlightAudio.maxSpotlights &&
                    spotlightsCount > spotlightAudio.maxSpotlights
                  ) {
                    spotlightsCount = 0;
                    handleAudioEnded();
                    return;
                  }
                  spotlightsCount++;

                  viewerApi.spotlight().apply({
                    selector: dataId,
                    mark: [
                      {
                        startOffset: sentence.textOffset,
                        length: sentence.textLength,
                        backgroundColor: spotlightAudio.sentenceHighlightColor,
                      },
                      {
                        startOffset: word.textOffset,
                        length: word.textLength,
                        backgroundColor: spotlightAudio.wordHighlightColor,
                      },
                    ],
                  });

                  return;
                }
              }
            }
          }
        }
      }
    };

    const handleAudioPaused = () => {
      viewerApi.spotlight().clear();
    };

    const handleAudioEnded = () => {
      // go to next page
      if (nextItem?.elementId) {
        setAudioURL("");
        setTTSJSON(null);
        viewerApi.route(nextItem?.elementId);
      }
    };

    const handleAudioCanPlay = () => {
      setAudioPlayerVisible(true);
    };

    const handleAudioIsWaiting = () => {
      setAudioPlayerVisible(false);
    };

    if (audioElement) {
      audioElement.addEventListener("timeupdate", handleTimeUpdate);
      audioElement.addEventListener("pause", handleAudioPaused);
      audioElement.addEventListener("ended", handleAudioEnded);
      audioElement.addEventListener("canplay", handleAudioCanPlay);
      audioElement.addEventListener("waiting", handleAudioIsWaiting);

      if (audioURL && ttsJSON) {
        audioRef.current?.play();
      }
    }

    return () => {
      if (audioElement) {
        audioElement.removeEventListener("timeupdate", handleTimeUpdate);
        audioElement.removeEventListener("pause", handleAudioPaused);
        audioElement.removeEventListener("ended", handleAudioEnded);
        audioElement.removeEventListener("canplay", handleAudioCanPlay);
        audioElement.removeEventListener("waiting", handleAudioIsWaiting);
      }
    };
  }, [audioURL, ttsJSON, viewerApi, nextItem, spotlightAudio]);

  //=================================================================================

  return (
    <div className="footer">
      <div className="audio-icon-container">
        <button className="audio-icon" onClick={handleAudioClick} />
      </div>

      {ttsJSON && audioURL && (
        <video
          className="audio-player"
          controls
          ref={audioRef}
          style={{ display: isAudioPlayerVisible ? "block" : "none" }}
        >
          <source src={audioURL} type="audio/mpeg" />
          Your browser does not support the audio.
        </video>
      )}

      <div className="navigation-bar">
        <div className="navigation-button previous-button">
          {prevItem && (
            <button onClick={onPrev}>
              ❮{" "}
              <span>
                {!!prevItem?.configurationFields && getTitle(prevItem.configurationFields)}
              </span>
            </button>
          )}
        </div>
        <input
          type="text"
          className="page-number"
          value={targetPageNumber}
          onChange={handleInputChange}
          onKeyUp={handleEnter}
        />
        <div className="navigation-button next-button">
          {nextItem && (
            <button onClick={onNext}>
              <span>
                {!!nextItem?.configurationFields && getTitle(nextItem.configurationFields)}
              </span>{" "}
              ❯
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
